import React, { useRef, useState } from 'react';
import { Box } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import PaginaSuscripcion from '../components/suscripcion/PaginaSuscripcion';
import BarraDePasos from '../components/suscripcion/BarraDePasos';
import TarjetaPlan from '../components/suscripcion/TarjetaPlan';
import TarjetaAU from '../components/suscripcion/TarjetaAU';
import CampoAU from '../components/suscripcion/CampoAU';
import EtiquetaAU from '../components/suscripcion/EtiquetaAU';
import LineaDeTiempo from '../components/suscripcion/LineaDeTiempo';
import BotonAU from '../components/suscripcion/BotonAU';
import ErrorConCodigo from '../components/ErrorConCodigo';
import usePlan from '../hooks/usePlan';
import { formatFechaConOffset } from '../utils/fechaSuscripcion';
import { validarDatosPersona } from '../utils/validacionSuscripcion';
import { AU, CONTORNO_FOCO, FUENTE, RADIO_CAMPO } from '../styles/tokensAU';

// Paso 1 de 2: tus datos. No llama al backend (salvo el monto del plan): valida y pasa a
// /subscribe/pago. Nombre y correo viajan por el estado del navegador, nunca por la URL.
const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = usePlan(location.search);
  // Al volver desde el paso 2 ("Editar mis datos") los campos vienen rellenos.
  const previo = location.state || {};
  const [datos, setDatos] = useState({ name: previo.name || '', email: previo.email || '' });
  const [errores, setErrores] = useState({});
  const formRef = useRef(null);
  const avisoPlanRef = useRef(null);

  const cambiar = (e) => {
    const { name, value } = e.target;
    setDatos((d) => ({ ...d, [name]: value }));
    if (errores[name]) setErrores((er) => ({ ...er, [name]: '' }));
  };

  const continuar = (e) => {
    e.preventDefault();
    const encontrados = validarDatosPersona(datos);
    setErrores(encontrados);

    // Sin avanzar, el foco va a lo que hay que arreglar: el primer campo con error o, si no, el aviso del plan.
    const primerCampo = ['name', 'email'].find((campo) => encontrados[campo]);
    if (primerCampo) {
      formRef.current?.elements.namedItem(primerCampo)?.focus();
      return;
    }
    if (plan.faltaPlan) {
      avisoPlanRef.current?.focus();
      return;
    }

    const q = new URLSearchParams();
    if (plan.planIdUrl) q.set('plan_id', plan.planIdUrl);
    q.set('start', plan.inicio);
    const persona = { name: datos.name.trim(), email: datos.email.trim() };
    // La entrada de /subscribe en el historial guarda los datos: con "atrás" en el navegador no se pierden.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: persona });
    navigate(`/subscribe/pago?${q.toString()}`, { state: persona });
  };

  const hitos = [
    { titulo: 'Días 1 y 2', detalle: 'Armo tu programa.' },
    { titulo: 'Día 3', fecha: formatFechaConOffset(plan.inicio, 3), detalle: 'Te entrego el programa y hacemos el onboarding.' },
    { titulo: 'Día 4', fecha: formatFechaConOffset(plan.inicio, 4), detalle: 'Empiezas a entrenar.' },
  ];

  return (
    <PaginaSuscripcion>
      <BarraDePasos paso={1} />

      <Box sx={{ textAlign: 'center' }}>
        <Box component="h1" sx={{ m: 0, fontFamily: FUENTE, fontSize: 26, fontWeight: 800, lineHeight: 1.15, color: AU.oscuro }}>
          Crea tu suscripción
        </Box>
        <Box sx={{ mt: 0.75, fontFamily: FUENTE, fontSize: 15, color: AU.grisMedio }}>
          Son 2 pasos: tus datos y cómo quieres pagar.
        </Box>
      </Box>

      <Box
        component="form"
        noValidate
        ref={formRef}
        onSubmit={continuar}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {plan.faltaPlan
          ? (
            <Box ref={avisoPlanRef} tabIndex={-1} sx={{ borderRadius: RADIO_CAMPO, '&:focus-visible': { ...CONTORNO_FOCO } }}>
              <ErrorConCodigo mensaje="Este link de suscripción no es válido. Pídele uno nuevo a Agustín por WhatsApp." />
            </Box>
          )
          : <TarjetaPlan monto={plan.monto} cargando={plan.cargando} error={plan.error} inicio={plan.inicio} />}

        <TarjetaAU sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CampoAU
            etiqueta="Nombre"
            name="name"
            value={datos.name}
            onChange={cambiar}
            requerido
            autoComplete="name"
            error={errores.name}
          />
          <CampoAU
            etiqueta="Email"
            name="email"
            tipo="email"
            value={datos.email}
            onChange={cambiar}
            requerido
            autoComplete="email"
            inputMode="email"
            error={errores.email}
          />
          <CampoAU
            etiqueta="Fecha de contratación"
            name="subscription_start"
            tipo="date"
            value={plan.inicio}
            soloLectura
            ayuda="*Fecha fija, definida por AU Coaching. El cobro se repite cada 30 días."
            sufijo={<LockOutlinedIcon aria-hidden="true" sx={{ fontSize: 20 }} />}
          />
        </TarjetaAU>

        <TarjetaAU>
          <EtiquetaAU component="h2" sx={{ m: 0 }}>Cómo funciona el inicio</EtiquetaAU>
          <Box sx={{ my: 1.5, fontFamily: FUENTE, fontSize: 14, color: AU.oscuro }}>
            El proceso arranca en la fecha de contratación de arriba. Desde ahí corren días corridos (incluye fines de semana).
          </Box>
          <LineaDeTiempo hitos={hitos} />
        </TarjetaAU>

        <BotonAU type="submit">Continuar</BotonAU>
      </Box>
    </PaginaSuscripcion>
  );
};

export default SubscriptionPage;
